import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AccessAdminService } from './access-admin.service';
import { SessionService } from './session.service';
import { AccessMe, StaffKey, StaffKeyInfo } from '../models/access.model';

export type StaffAccessStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Claves de staff de la sesión (eje staff, GET /access/me).
 *
 * Cada sección del backoffice exige, además del rol `providerAdmin`, su
 * clave de staff (`<área>.read` para ver, `<área>.manage` para escribir).
 * Las claves son por persona y se configuran en «Equipo»
 * (support-access.spec.md §1); una cuenta sin configurar las tiene todas salvo
 * las explicit (p. ej. `salesTrial.manage`, que se asigna a mano).
 *
 * Falla cerrado: si /access/me no responde, o la sesión no es de staff,
 * `has()` devuelve false y las secciones quedan cerradas con un mensaje.
 * El API sigue siendo la autoridad; esto sólo evita pantallas inútiles.
 */
@Injectable({ providedIn: 'root' })
export class StaffAccessService {
  private readonly api = inject(AccessAdminService);
  private readonly session = inject(SessionService);

  readonly status = signal<StaffAccessStatus>('idle');
  readonly me = signal<AccessMe | null>(null);
  /** Motivo legible de la última falla (vacío si no hubo). */
  readonly failure = signal('');

  private inflight$: Observable<AccessMe | null> | null = null;

  constructor() {
    // Al cerrar sesión (o si el interceptor la cierra) se olvidan las claves.
    this.session.profile.subscribe((profile) => {
      if (!profile) this.reset();
    });
  }

  /** ¿La sesión tiene esta clave de staff? Falla cerrado. */
  has(key: StaffKey): boolean {
    const me = this.me();
    return this.status() === 'ready' && me?.principal === 'staff' && me.staff.includes(key);
  }

  /** Textos de una clave de staff según /access/me (null si no respondió o no la trae). */
  keyInfo(key: string): StaffKeyInfo | null {
    return this.me()?.staffKeys?.find((info) => info.key === key) ?? null;
  }

  /** `'companies.manage'` → «Empresas · Editar» con los textos del API; sin ellos, la clave. */
  shortLabel(key: string): string {
    const info = this.keyInfo(key);
    return info?.areaLabel ? `${info.areaLabel} · ${info.verbLabel}` : key;
  }

  /** Motivo por el que falta una clave, para el aviso de sección cerrada. */
  reasonFor(key: StaffKey): string {
    if (this.status() === 'error') {
      return (
        this.failure() ||
        'No fue posible verificar tus permisos de staff. Por seguridad la sección queda cerrada.'
      );
    }
    if (this.status() !== 'ready') return 'Verificando tus permisos de staff…';
    const me = this.me();
    if (me?.principal !== 'staff') {
      return 'Tu sesión no es de staff: el API no te reconoce como parte del equipo del proveedor.';
    }
    return `Tu cuenta de staff no tiene el permiso «${this.shortLabel(key)}» (${key}).`;
  }

  /** GET /access/me. Nunca emite error: ante una falla deja el estado en `error`. */
  load(): Observable<AccessMe | null> {
    this.status.set('loading');
    this.failure.set('');
    this.inflight$ = this.api.getMe().pipe(
      map((me) => {
        this.me.set(me);
        this.status.set('ready');
        return me as AccessMe | null;
      }),
      catchError((err) => {
        this.me.set(null);
        this.status.set('error');
        this.failure.set(describeLoadFailure(err?.status));
        return of(null);
      }),
      finalize(() => (this.inflight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inflight$;
  }

  /**
   * Arranque con una sesión restaurada de localStorage: primero /auth/me y
   * luego /access/me, encadenados (mismo orden que la webapp).
   */
  bootstrap(): Observable<AccessMe | null> {
    return this.session.loadProfile().pipe(
      switchMap((profile) => {
        if (profile) return this.load();
        // Sin sesión no hay a quién verificar: queda cerrado.
        this.me.set(null);
        this.status.set('error');
        this.failure.set('No hay una sesión activa.');
        return of(null);
      }),
    );
  }

  /** Para guards y vistas: resuelve cuando hay respuesta (o falla) de /access/me. */
  ensureLoaded(): Observable<AccessMe | null> {
    const status = this.status();
    if (status === 'ready' || status === 'error') return of(this.me());
    if (status === 'loading' && this.inflight$) return this.inflight$;
    this.status.set('loading');
    this.inflight$ = this.bootstrap().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    return this.inflight$;
  }

  reset(): void {
    this.status.set('idle');
    this.me.set(null);
    this.failure.set('');
    this.inflight$ = null;
  }
}

function describeLoadFailure(status: number | undefined): string {
  if (status === 0) {
    return 'No fue posible conectar con el API para verificar tus permisos de staff (GET /access/me). La sección queda cerrada.';
  }
  if (status === 401 || status === 403) {
    return 'El API rechazó la verificación de tus permisos de staff. Vuelve a iniciar sesión.';
  }
  return 'No fue posible verificar tus permisos de staff (GET /access/me). Por seguridad la sección queda cerrada.';
}
